import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Space,
  Popconfirm,
  Tag,
  Descriptions,
  Checkbox,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  ReloadOutlined,
  EyeOutlined,
  EditOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { apiClient } from '../../../config/apiClient';
import MainLayout from '../../layout/layout';
import { usePermissions } from '../../../context/permissionsContext';

const TIPO_USUARIO_OPTS = [
  { value: 1, label: 'Administrador' },
  { value: 2, label: 'Alumno' },
  { value: 3, label: 'Jugador' },
];

const GestionUsuarios = () => {
  const { hasPermission } = usePermissions();
  const [usuarios, setUsuarios] = useState([]);
  const [roles, setRoles] = useState([]);
  const [modulos, setModulos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalDetalleVisible, setModalDetalleVisible] = useState(false);
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [incluirInactivos, setIncluirInactivos] = useState(false);
  const [form] = Form.useForm();

  const loadUsuarios = async () => {
    setLoading(true);
    try {
      const params = incluirInactivos ? '?incluir_inactivos=true' : '';
      const res = await apiClient.get(`/gestion/usuarios${params}`);
      if (res.ok) {
        const data = await res.json();
        setUsuarios(Array.isArray(data) ? data : []);
      } else {
        message.error('Error al cargar usuarios');
      }
    } catch (e) {
      console.error(e);
      message.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      const res = await apiClient.get('/gestion/roles');
      if (res.ok) {
        const data = await res.json();
        setRoles(Array.isArray(data) ? data : []);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const loadModulos = async () => {
    try {
      const res = await apiClient.get('/gestion/permisos/modulos');
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data)) {
          setModulos(data);
        } else if (data && typeof data === 'object') {
          setModulos(Object.keys(data).map((modulo) => ({ modulo, permisos: data[modulo] || [] })));
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    loadUsuarios();
    loadRoles();
    loadModulos();
  }, []);

  useEffect(() => {
    loadUsuarios();
  }, [incluirInactivos]);

  const handleOpenCreate = () => {
    setEditingId(null);
    setUsuarioSeleccionado(null);
    form.resetFields();
    form.setFieldsValue({ tipoUsuario: 1, role_ids: [] });
    setModalVisible(true);
  };

  const handleOpenEdit = async (record) => {
    setEditingId(record.usu_id);
    setUsuarioSeleccionado(record);
    try {
      const res = await apiClient.get(`/gestion/usuarios/${record.usu_id}`);
      if (res.ok) {
        const u = await res.json();
        form.setFieldsValue({
          usuario: u.usu_usuario,
          tipoUsuario: u.usu_tipousuario,
          usu_activo: u.usu_activo !== false,
          role_ids: (u.roles || []).map((r) => r.rol_id),
        });
        setModalVisible(true);
      } else {
        message.error('Error al cargar usuario');
      }
    } catch (e) {
      message.error('Error al cargar usuario');
    }
  };

  const handleVerDetalle = (record) => {
    setUsuarioSeleccionado(record);
    setModalDetalleVisible(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (editingId) {
        const body = {
          usuario: values.usuario,
          tipoUsuario: values.tipoUsuario,
          usu_activo: values.usu_activo,
          role_ids: values.role_ids || [],
        };
        if (values.contrasenia && String(values.contrasenia).trim()) {
          body.contrasenia = values.contrasenia;
        }
        const res = await apiClient.put(`/gestion/usuarios/${editingId}`, body);
        if (res.ok) {
          message.success('Usuario actualizado');
          setModalVisible(false);
          loadUsuarios();
        } else {
          const err = await res.json().catch(() => ({}));
          message.error(err.message || 'Error al actualizar');
        }
      } else {
        if (!values.contrasenia || !String(values.contrasenia).trim()) {
          message.error('La contraseña es obligatoria al crear');
          return;
        }
        const res = await apiClient.post('/gestion/usuarios', {
          usuario: values.usuario,
          contrasenia: values.contrasenia,
          tipoUsuario: values.tipoUsuario ?? 1,
          role_ids: values.role_ids || [],
        });
        if (res.ok) {
          message.success('Usuario creado');
          setModalVisible(false);
          loadUsuarios();
        } else {
          const err = await res.json().catch(() => ({}));
          message.error(err.message || 'Error al crear');
        }
      }
    } catch (e) {
      if (e.errorFields) return;
      message.error(e.message || 'Error al guardar');
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await apiClient.delete(`/gestion/usuarios/${id}`);
      if (res.ok) {
        message.success('Usuario desactivado correctamente');
        loadUsuarios();
      } else {
        const err = await res.json().catch(() => ({}));
        message.error(err.message || 'Error al eliminar');
      }
    } catch (e) {
      message.error('Error al eliminar');
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'usu_id',
      key: 'usu_id',
      width: 70,
    },
    {
      title: 'Usuario',
      dataIndex: 'usu_usuario',
      key: 'usu_usuario',
    },
    {
      title: 'Tipo',
      dataIndex: 'tiu_descripcion',
      key: 'tiu_descripcion',
      render: (t) => t || '-',
    },
    {
      title: 'Activo',
      dataIndex: 'usu_activo',
      key: 'usu_activo',
      width: 90,
      render: (v) => (v !== false ? <Tag color="green">Sí</Tag> : <Tag color="red">No</Tag>),
    },
    {
      title: 'Roles',
      key: 'roles',
      render: (_, record) => (
        <>
          {(record.roles || []).map((r) => (
            <Tag key={r.rol_id}>{r.rol_nombre}</Tag>
          ))}
          {(record.roles || []).length === 0 && '-'}
        </>
      ),
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 220,
      render: (_, record) => (
        <Space>
          <Button type="link" icon={<EyeOutlined />} onClick={() => handleVerDetalle(record)}>
            Ver
          </Button>
          {hasPermission('usuarios', 'editar') && (
            <Button type="link" icon={<EditOutlined />} onClick={() => handleOpenEdit(record)}>
              Editar
            </Button>
          )}
          {hasPermission('usuarios', 'eliminar') && record.usu_activo !== false && (
            <Popconfirm
              title="¿Desactivar este usuario? No se borrará, quedará inactivo."
              onConfirm={() => handleDelete(record.usu_id)}
              okText="Sí"
              cancelText="No"
            >
              <Button type="link" danger icon={<DeleteOutlined />}>
                Eliminar
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <MainLayout>
      <div style={{ padding: '24px' }}>
        <div style={{ marginBottom: 16 }}>
          <h1 style={{ margin: 0, marginBottom: 16, color: '#fff', fontSize: 24 }}>
            Gestión de Usuarios
          </h1>
          <Space wrap style={{ marginBottom: 16 }}>
            <Button icon={<ReloadOutlined />} onClick={loadUsuarios} loading={loading}>
              Actualizar
            </Button>
            {hasPermission('usuarios', 'crear') && (
              <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenCreate}>
                Nuevo usuario
              </Button>
            )}
            <Checkbox
              checked={incluirInactivos}
              onChange={(e) => setIncluirInactivos(e.target.checked)}
              style={{ color: '#b0b0b0' }}
            >
              Incluir inactivos
            </Checkbox>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={usuarios}
          loading={loading}
          rowKey="usu_id"
          pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (t) => `Total: ${t} usuarios` }}
        />

        <Modal
          title={editingId ? 'Editar usuario' : 'Nuevo usuario'}
          open={modalVisible}
          onOk={handleSave}
          onCancel={() => setModalVisible(false)}
          okText="Guardar"
          cancelText="Cancelar"
          width={500}
        >
          <Form form={form} layout="vertical">
            <Form.Item
              name="usuario"
              label="Nombre de usuario"
              rules={[{ required: true, message: 'Requerido' }]}
            >
              <Input prefix={<UserOutlined />} placeholder="Usuario" disabled={!!editingId} />
            </Form.Item>
            {!editingId && (
              <Form.Item
                name="contrasenia"
                label="Contraseña"
                rules={[{ required: true, message: 'Requerida al crear' }]}
              >
                <Input.Password placeholder="Contraseña" />
              </Form.Item>
            )}
            {editingId && (
              <Form.Item name="contrasenia" label="Nueva contraseña (dejar en blanco para no cambiar)">
                <Input.Password placeholder="Opcional" />
              </Form.Item>
            )}
            <Form.Item name="tipoUsuario" label="Tipo de usuario" rules={[{ required: true }]}>
              <Select options={TIPO_USUARIO_OPTS} placeholder="Tipo" />
            </Form.Item>
            {editingId && (
              <Form.Item name="usu_activo" valuePropName="checked" label="Activo">
                <Checkbox>Usuario activo</Checkbox>
              </Form.Item>
            )}
            <Form.Item name="role_ids" label="Roles">
              <Select
                mode="multiple"
                placeholder="Seleccione roles"
                optionFilterProp="label"
                options={roles.map((r) => ({ value: r.rol_id, label: r.rol_nombre }))}
              />
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          title="Detalle de usuario"
          open={modalDetalleVisible}
          onCancel={() => setModalDetalleVisible(false)}
          footer={null}
          width={560}
        >
          {usuarioSeleccionado && (
            <>
              <Descriptions column={1} bordered>
                <Descriptions.Item label="ID">{usuarioSeleccionado.usu_id}</Descriptions.Item>
                <Descriptions.Item label="Usuario">{usuarioSeleccionado.usu_usuario}</Descriptions.Item>
                <Descriptions.Item label="Tipo">
                  {usuarioSeleccionado.tiu_descripcion || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Activo">
                  {usuarioSeleccionado.usu_activo !== false ? 'Sí' : 'No'}
                </Descriptions.Item>
                <Descriptions.Item label="Roles">
                  {(usuarioSeleccionado.roles || []).map((r) => (
                    <Tag key={r.rol_id}>{r.rol_nombre}</Tag>
                  ))}
                  {(usuarioSeleccionado.roles || []).length === 0 && '-'}
                </Descriptions.Item>
              </Descriptions>
              {modulos.length > 0 && (
                <>
                  <div style={{ marginTop: 16, marginBottom: 8, color: '#b0b0b0', fontSize: 12 }}>
                    Los permisos se derivan de los roles asignados. Módulos del sistema:
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {modulos.map((m) => (
                      <Tag key={m.modulo}>{m.modulo}</Tag>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </Modal>
      </div>
    </MainLayout>
  );
};

export default GestionUsuarios;
